import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowDownNarrowWide,
  CheckCircle2,
  Clock,
  History,
  Search,
  XCircle,
} from "lucide-react";
import {
  recentJobs,
  type BridgeJob,
  type JobKind,
  type JobStatus,
} from "@/lib/api";

type KindFilter = "all" | JobKind;
type StatusFilter = "all" | JobStatus;
type SortKey = "newest" | "oldest" | "kind";

/**
 * Recent job feed — backed by the in-memory ring in JobRegistry. Wave 2
 * swaps the registry for a Postgres table; this page does not need to
 * change because the JSON shape stays identical.
 *
 * <p>Filter / search / sort happens client-side. The full page rarely
 * holds more than the ring's 50-entry cap, so additional server-side
 * pagination would be over-engineered for the current data volume.
 */
export function HistoryPage() {
  const { data, isLoading, error, refetch } = useQuery<BridgeJob[]>({
    queryKey: ["bridge-jobs", "recent"],
    queryFn: () => recentJobs(50),
    refetchInterval: 5000,
  });

  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState<KindFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sort, setSort] = useState<SortKey>("newest");

  const filtered = useMemo(() => {
    if (!data) return [];
    let out = [...data];
    if (search.trim()) {
      const needle = search.trim().toLowerCase();
      out = out.filter((j) =>
        (j.submittedBy ?? "").toLowerCase().includes(needle) ||
        j.id.toLowerCase().includes(needle) ||
        (j.ir?.displayName ?? "").toLowerCase().includes(needle) ||
        (j.errorMessage ?? "").toLowerCase().includes(needle),
      );
    }
    if (kindFilter !== "all") {
      out = out.filter((j) => j.kind === kindFilter);
    }
    if (statusFilter !== "all") {
      out = out.filter((j) => j.status === statusFilter);
    }
    switch (sort) {
      case "newest":
        out.sort((a, b) => b.submittedAt.localeCompare(a.submittedAt));
        break;
      case "oldest":
        out.sort((a, b) => a.submittedAt.localeCompare(b.submittedAt));
        break;
      case "kind":
        out.sort((a, b) =>
          a.kind.localeCompare(b.kind) || b.submittedAt.localeCompare(a.submittedAt),
        );
        break;
    }
    return out;
  }, [data, search, kindFilter, statusFilter, sort]);

  const total = data?.length ?? 0;

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-bridge-ink">Recent activity</h1>
          <p className="mt-0.5 text-sm text-bridge-slate">
            Last 50 parse / translate / validate runs on this server. Server-side history is
            in-memory until Wave 2 ships persistent jobs.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="rounded-md border border-bridge-slate/30 bg-white px-3 py-1 text-xs font-medium text-bridge-ink hover:bg-bridge-mist"
        >
          Refresh
        </button>
      </header>

      {total > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl2 border border-bridge-slate/20 bg-white p-3 shadow-card">
          <div className="relative flex-1 min-w-[14rem]">
            <Search
              size={14}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-bridge-slate"
              aria-hidden
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by user, asset, error, or job id…"
              aria-label="Search jobs"
              className="w-full rounded-md border border-bridge-slate/30 bg-white py-1.5 pl-8 pr-3 text-sm text-bridge-ink placeholder:text-bridge-slate focus:border-bridge-primary focus:outline-none focus:ring-2 focus:ring-bridge-primary/20"
            />
          </div>
          <Select
            label="Kind"
            value={kindFilter}
            onChange={(v) => setKindFilter(v as KindFilter)}
            options={[
              ["all", "Any kind"],
              ["PARSE", "Parse"],
              ["TRANSLATE", "Translate"],
              ["VALIDATE", "Validate"],
            ]}
          />
          <Select
            label="Status"
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as StatusFilter)}
            options={[
              ["all", "Any status"],
              ["QUEUED", "Queued"],
              ["RUNNING", "Running"],
              ["SUCCEEDED", "Succeeded"],
              ["FAILED", "Failed"],
            ]}
          />
          <Select
            label="Sort"
            icon={<ArrowDownNarrowWide size={12} />}
            value={sort}
            onChange={(v) => setSort(v as SortKey)}
            options={[
              ["newest", "Newest first"],
              ["oldest", "Oldest first"],
              ["kind", "By kind"],
            ]}
          />
          <span className="ml-auto text-2xs text-bridge-slate" aria-live="polite">
            Showing <span className="font-medium text-bridge-ink">{filtered.length}</span> of{" "}
            <span className="font-medium text-bridge-ink">{total}</span>
          </span>
        </div>
      )}

      {isLoading && (
        <p className="text-sm text-bridge-slate">Loading…</p>
      )}
      {error && (
        <p className="rounded-md border border-bridge-danger/30 bg-bridge-danger/5 p-3 text-sm text-bridge-danger">
          Failed to load history — {(error as Error).message}
        </p>
      )}

      {data && total === 0 && (
        <div className="rounded-xl2 border border-dashed border-bridge-slate/30 bg-white p-8 text-center text-sm text-bridge-slate">
          <History className="mx-auto mb-2" size={28} />
          No runs yet. Parse a Cognos report to see activity here.
        </div>
      )}
      {data && total > 0 && filtered.length === 0 && (
        <div className="rounded-xl2 border border-dashed border-bridge-slate/30 bg-white p-8 text-center text-sm text-bridge-slate">
          No jobs match your filters. Clear the search or widen the filters.
        </div>
      )}

      {filtered.length > 0 && (
        <ul data-tour-id="history-list" className="grid gap-2">
          {filtered.map((j) => (
            <JobRow key={j.id} job={j} />
          ))}
        </ul>
      )}
    </div>
  );
}

function JobRow({ job }: { job: BridgeJob }) {
  const tone =
    job.status === "SUCCEEDED"
      ? "border-emerald-200 bg-emerald-50/40"
      : job.status === "FAILED"
      ? "border-rose-200 bg-rose-50/40"
      : "border-bridge-slate/20 bg-white";

  const icon =
    job.status === "SUCCEEDED" ? <CheckCircle2 size={14} className="text-emerald-600" />
    : job.status === "FAILED" ? <XCircle size={14} className="text-rose-600" />
    : <Clock size={14} className="text-bridge-slate" />;

  const subtitle =
    job.kind === "PARSE" && job.ir
      ? `${job.ir.displayName} — ${job.ir.fields.length} fields, ${job.ir.visualizations.length} visuals`
      : job.kind === "TRANSLATE" && job.translation
      ? `${job.translation.target} — ${job.translation.unmappedConcepts.length} unmapped, conf ${(job.translation.confidence * 100).toFixed(0)}%`
      : job.kind === "VALIDATE" && job.validation
      ? `${(job.validation.findings ?? []).length} finding(s)`
      : job.errorMessage ?? "";

  return (
    <li className={`flex items-center gap-3 rounded-md border px-3 py-2 text-xs ${tone}`}>
      <span aria-hidden>{icon}</span>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-mono text-2xs uppercase tracking-wide text-bridge-slate">
            {job.kind}
          </span>
          <span className="font-medium text-bridge-ink">{subtitle || job.id}</span>
        </div>
        <div className="mt-0.5 text-2xs text-bridge-slate">
          {job.submittedBy ?? "anonymous"} · {new Date(job.submittedAt).toLocaleString()}
        </div>
      </div>
      <span className="font-mono text-2xs text-bridge-slate" title={job.id}>
        {job.id.slice(0, 8)}
      </span>
    </li>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
  icon,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: [string, string][];
  icon?: React.ReactNode;
}) {
  return (
    <label className="inline-flex items-center gap-1.5 text-2xs text-bridge-slate">
      {icon}
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        className="rounded-md border border-bridge-slate/30 bg-white px-2 py-1 text-2xs text-bridge-ink focus:border-bridge-primary focus:outline-none focus:ring-2 focus:ring-bridge-primary/20"
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
}
